import { VISITED_STATUSES } from '../types';
import type { OrderEventKind } from './model';

/** Najwięcej par w migawce zdarzenia seeded (snapshotOf daje domyślnie 300). */
export const MAX_SNAPSHOT_ENTRIES = 400;
/** Najwięcej wpisów w jednym zdarzeniu placed. */
export const MAX_PLACEMENTS = 100;
/** Tak samo jak identyfikatory w protokole synchronizacji. */
const MAX_ID_LENGTH = 100;

const STATUSES = new Set<unknown>(VISITED_STATUSES);

type Fields = Record<string, unknown>;
type Check = (e: Fields) => string | null;

const isObject = (v: unknown): v is Fields => typeof v === 'object' && v !== null && !Array.isArray(v);
const isId = (v: unknown): v is string => typeof v === 'string' && v.length > 0 && v.length <= MAX_ID_LENGTH;
const isRef = (v: unknown) => v === null || isId(v);

/** Nazwa pierwszego pola, które nie spełnia warunku, albo null. */
const firstBad = (e: Fields, test: (v: unknown) => boolean, ...names: string[]) =>
  names.find((n) => !test(e[n])) ?? null;

const SHAPES: Record<OrderEventKind, Check> = {
  item_checked: (e) =>
    firstBad(e, isId, 'visitId', 'listItemId', 'productId') ??
    firstBad(e, (v) => STATUSES.has(v), 'status') ??
    firstBad(e, Number.isFinite, 'seq', 'wallMs'),
  undo: (e) => firstBad(e, isId, 'visitId', 'targetId'),
  visit_finished: (e) => firstBad(e, isId, 'visitId'),
  manual_move: (e) => firstBad(e, isId, 'productId') ?? firstBad(e, isRef, 'afterId', 'beforeId'),
  seeded: (e) => {
    if (!isId(e.fromStoreId)) return 'fromStoreId';
    if (e.ifEmpty !== undefined && typeof e.ifEmpty !== 'boolean') return 'ifEmpty';
    const s = e.snapshot;
    const ok =
      Array.isArray(s) &&
      s.length <= MAX_SNAPSHOT_ENTRIES &&
      s.every((p) => Array.isArray(p) && p.length === 2 && isId(p[0]) && Number.isFinite(p[1]));
    return ok ? null : 'snapshot';
  },
  placed: (e) => {
    if (e.source !== 'ai') return 'source';
    const p = e.placements;
    const ok =
      Array.isArray(p) &&
      p.length > 0 &&
      p.length <= MAX_PLACEMENTS &&
      p.every((x) => isObject(x) && isId(x.productId) && isRef(x.afterId));
    return ok ? null : 'placements';
  },
  reset: () => null,
};

/**
 * Sprawdza kształt zdarzenia kolejności przed zapisem (telefon) i przy przyjęciu (serwer) — obie strony
 * stosują te same reguły, bo zdarzenie odrzucone przez serwer zostałoby tylko w jednym telefonie.
 * Dodatkowe pola są dozwolone. Zwraca null albo powód odrzucenia.
 */
export function validateOrderEvent(e: unknown): string | null {
  if (!isObject(e)) return 'zdarzenie nie jest obiektem';
  const bad = firstBad(e, isId, 'id', 'storeId', 'deviceId') ?? firstBad(e, (v) => typeof v === 'string' && v !== '', 'hlc');
  if (bad) return `nieprawidłowe pole ${bad}`;
  if (typeof e.kind !== 'string' || !Object.hasOwn(SHAPES, e.kind)) return 'nieznany rodzaj zdarzenia';
  const field = SHAPES[e.kind as OrderEventKind](e);
  return field === null ? null : `nieprawidłowe pole ${field} w zdarzeniu ${e.kind}`;
}
